import { t } from '@/utils/i18n'
import React, { useEffect, useState } from 'react'
import { useUpdateEffect } from 'ahooks'
import { InfiniteScroll, List, Toast } from 'antd-mobile'
import Masonry from 'react-masonry-css'
import useCorpInfoStore from '@/stores/corp-info'
import { useCartStore } from '@/stores'
import type { RequestParams } from '@/types/product'
import type {
  ProductItem,
  SaleProduct,
  ShowPlaceOrderProps,
} from '@/types/product'
import ProductCell from '@/components/ProductCell/ProductCell'
import { apis, utils, productUtil } from '@/utils'
import eventUtil from '@/utils/event'
import styles from './ProductList.module.less'
import useRefState from '@/hooks/useRefState'
import PlaceOrder from '@/components/PlaceOrder/PlaceOrder'

// 导入图片
import iconNoData from '@/assets/img/default/empty_list.jpg'

interface ProductListProps {
  listMode: boolean // 列表模式 or 双列
  requestParams: RequestParams
  isFilterPage?: boolean
}

const { productApi, orderApi } = apis
const { to, isDisableBodyScroll, cloneDeep } = utils
const { editSaleUnit, editSaleQuantity } = productUtil
let pageNum = 1

const ProductList: React.FC<ProductListProps> = (props) => {
  const { listMode, requestParams, isFilterPage } = props
  const { corpInfo } = useCorpInfoStore()
  const { cart, setCart } = useCartStore()
  const { corpId, customerId } = corpInfo
  const { cartId } = cart
  const [products, setProducts, productsRef] = useRefState<ProductItem[]>([])
  const [loading, setLoading] = useState(true)
  const [noData, setNoData] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [updateKey, setUpdateKey] = useState('0') // 因为这个 https://github.com/ant-design/ant-design-mobile/issues/5360 bug,所以加了一个 key
  const [showPlaceOrder, setShowPlaceOrder] = useState(false)
  const [activeProduct, setActiveProduct] = useState<ProductItem>()
  const [activeIndex, setActiveIndex] = useState(0)

  function handleShowPlaceOrder(option: ShowPlaceOrderProps) {
    const { showPlaceOrder, index, product } = option
    setShowPlaceOrder(showPlaceOrder)
    setActiveIndex(index)
    setActiveProduct(product)
  }

  // 删除购物车货品后更新 SaleProduct
  function updateProduct(saleProd?: SaleProduct) {
    const productItem = products[activeIndex]
    if (productItem) {
      if (saleProd) {
        productItem.saleProduct = saleProd
      } else {
        productItem.saleProduct = null
      }

      setProducts([...products])
    }
  }

  function handleShowUnitModal() {
    console.log('handleShowUnitModal')
  }

  async function loadMore() {
    pageNum = pageNum + 1
    return getProductList(pageNum)
  }

  function setStatus(index: number, status: string) {
    products[index].status = status

    setProducts([...products])
  }

  // 删除货品
  async function requestDeleteProd(product: ProductItem, index: number) {
    const saleProduct = product.saleProduct || {}
    setStatus(index, 'pending')

    const [err, res] = await to(
      orderApi.deleteProd({
        corpId: saleProduct.corpId,
        cartId: saleProduct.cartId,
        saleProductIds: [saleProduct.saleProductId],
      }),
    )

    if (err) {
      Toast.show(err.message || err.msg)
      setStatus(index, 'fail')
      return
    }

    const { productCount } = res.data
    setStatus(index, 'success')
    products[index].saleProduct = null

    // 更新货品下单信息
    setProducts([...products])
    updateCartProductCount(productCount)
  }

  // 请求添加/更新货品
  async function requestAddProd(product: ProductItem, index: number) {
    const params = {
      saleProduct: {
        ...(product.saleProduct || {}),
        selected: 1,
      },
    }

    setStatus(index, 'pending')

    const [err, res] = await to(orderApi.addUpdate(params))

    if (err) {
      Toast.show(err.message || err.msg)
      setStatus(index, 'fail')
      return
    }

    setStatus(index, 'success')
    const { saleProduct, productCount } = res.data
    products[index].saleProduct = saleProduct
    products[index].cloudProduct = product.cloudProduct

    // 更新货品下单信息
    setProducts([...products])
    updateCartProductCount(productCount)
  }

  // 更新货品信息
  function handleUpdateProd(quantity: string, index: number) {
    const productItem = cloneDeep(products[index])

    /**
     * detail: {
     *  productMoqUnitIndex: 选择的起订单位索引,
     *  quantity: 下单数量
     * }
     * */
    const { productMoqUnitIndex } = productItem.cloudProduct

    // 修改选择单位索引
    if (productMoqUnitIndex !== undefined) {
      editSaleUnit(productItem, productMoqUnitIndex)
    }

    // 没有数量传过来直接更新单位就行了
    if (quantity === void 0) {
      products[index].cloudProduct = productItem.cloudProduct
      return
    }

    // 只要有quantity就应该发请求,没有则不发
    if (Number(quantity) === 0) {
      // 数量改为0要删除货品
      requestDeleteProd(productItem, index)
      return
    }

    editSaleQuantity(productItem, Number(quantity))

    productItem.saleProduct.cartId = cartId
    productItem.saleProduct.corpId = corpId

    requestAddProd(productItem, index)
  }

  function updateCartProductCount(productCount: number) {
    cart.productCount = productCount
    setCart(cart)
  }

  /**
   *
   * @param productList 需要更新的货品列表; item: {productId, saleProduct}
   */
  function handleUpdateProduct(productList: any[], isfresh = false) {
    if (isfresh) {
      pageNum = 1
      getProductList(pageNum)
      return
    }

    if (productList.length) {
      const currentProducts = productsRef.current
      productList.forEach((productItem) => {
        const { productId, saleProduct = {} } = productItem
        const index = currentProducts.findIndex(
          (item: ProductItem) => item.cloudProduct.productId === productId,
        )

        if (index > -1) {
          currentProducts[index].saleProduct = saleProduct
          setProducts(currentProducts.concat([]))
        }
      })

      setProducts(currentProducts.concat([]))
    }
  }

  useUpdateEffect(() => {
    ;(async () => {
      pageNum = 1
      setHasMore(false)
      setProducts([])
      setNoData(false)
      await getProductList(pageNum)
      setUpdateKey(new Date().getTime().toString() + Math.random() * 10)
    })()
  }, [requestParams])

  async function getProductList(pageNum = 1) {
    setLoading(true)

    const [error, res] = await to(
      productApi.prodList({
        corpId,
        cartId,
        pageSize: 20,
        ...requestParams,
        customerId,
        pageNum,
      }),
    )

    setLoading(false)

    const { pages, code } = res
    const list = res.data || []

    if (error) {
      Toast.show({ content: error.msg })
      return
    }

    // 倒序循环（因为删除元素会导致下标变化，只能用倒序循环）
    for (let index = list.length - 1; index >= 0; index--) {
      const item = list[index]

      const {
        cloudProduct: { unitPriceArray, productMoqUnitIndex },
      } = item

      // 删除cloudProduct.unitPriceArray为空的货品（异常数据）
      if (!unitPriceArray || !unitPriceArray.length) {
        list.splice(index, 1)
        continue
      }

      // 后端存在数据维护不正确的问题，起订单位的下标大于等于单位数组的长度时，需要重置下标为0
      if (
        productMoqUnitIndex > 0 &&
        productMoqUnitIndex >= unitPriceArray.length
      ) {
        item.cloudProduct.productMoqUnitIndex = 0
      }
    }

    if (pageNum === 1) {
      setProducts(list || [])
    } else {
      setProducts(products.concat(...(list || [])))
    }

    if (
      (Array.isArray(list) && pageNum >= pages) ||
      error ||
      (code === 0 && !list)
    ) {
      setHasMore(false)
    } else {
      setHasMore(true)
    }

    if (pageNum === 1 && list.length === 0) {
      setNoData(true)
    }
  }

  useUpdateEffect(() => {
    if (products.length) {
      eventUtil.on('updateProduct', handleUpdateProduct)
      return () => {
        eventUtil.off('updateProduct', handleUpdateProduct)
      }
    }
  }, [products])

  useEffect(() => {
    // 禁用 body 滚动
    isDisableBodyScroll(showPlaceOrder)
  }, [showPlaceOrder])

  const breakpointColumnsObj = {
    default: 2,
  }

  return (
    <div key={updateKey} style={{ width: '100%', height: '100%' }}>
      {/* 列表模式 */}
      {listMode && (
        <List
          className={styles['customize-style']}
          style={{
            '--padding-left': '0',
            '--padding-right': '0',
            '--border-bottom': 'none',
            '--border-top': 'none',
            '--border-inner': 'none',
          }}
        >
          {products.map((item, index) => (
            <List.Item key={item.cloudProduct.productId}>
              <ProductCell
                listMode={listMode}
                item={item}
                isFilterPage={isFilterPage}
                index={index}
                searchText={requestParams.searchText || ''}
                onShowPlaceOrder={handleShowPlaceOrder}
                onShowUnitModal={handleShowUnitModal}
                onUpdateProd={handleUpdateProd}
              />
            </List.Item>
          ))}
        </List>
      )}

      {/* 瀑布流模式 */}
      {!listMode && (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className={styles['my-masonry-grid']}
          columnClassName={styles['my-masonry-grid_column']}
        >
          {products.map((item, index) => (
            <ProductCell
              key={item.cloudProduct.productId}
              listMode={listMode}
              item={item}
              isFilterPage={isFilterPage}
              index={index}
              searchText={requestParams.searchText || ''}
              onShowPlaceOrder={handleShowPlaceOrder}
              onShowUnitModal={handleShowUnitModal}
              onUpdateProd={handleUpdateProd}
            />
          ))}
        </Masonry>
      )}

      {loading && pageNum === 1 && (
        <div
          className={styles['product-list-skeleton']}
          style={{ height: '100vh' }}
        ></div>
      )}

      {hasMore && <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />}

      {!loading && noData ? (
        <div className={styles['no-result']}>
          <img src={iconNoData} />
          <p>{t('暂无货品')}</p>
        </div>
      ) : (
        ''
      )}

      {!loading && !noData && !hasMore ? (
        <p className={styles['more-msg']}>{t('--- 已经到底啦 ---')}</p>
      ) : (
        ''
      )}
      {showPlaceOrder && activeProduct ? (
        <PlaceOrder
          showPlaceOrder={showPlaceOrder}
          item={{
            cloudProduct: activeProduct?.cloudProduct,
            saleProduct: activeProduct?.saleProduct,
          }}
          onClosePlaceOrder={() => setShowPlaceOrder(false)}
          onRemoveCartProduct={updateProduct}
          onUpdateProd={updateProduct}
        />
      ) : (
        ''
      )}
    </div>
  )
}

export default ProductList
